import { CheckIcon } from 'lucide-react'
import { cn } from '@/lib/utils'

interface Step {
    label: string
    icon?: React.ReactNode
}

interface StepIndicatorProps {
    steps: Step[]
    currentStep: number
    onChange?: (step: number) => void
}

/**
 * Custom Step Indicator - driver.js compatible
 * Simple horizontal stepper without complex CSS transforms
 */
const StepIndicator = ({
    steps,
    currentStep,
    onChange,
}: StepIndicatorProps) => {
    return (
        <div className="w-full py-3 px-4 bg-white" data-tour="step-indicator">
            <div className="flex items-start">
                {steps.map((step, index) => {
                    const stepNumber = index + 1
                    const isActive = stepNumber === currentStep
                    const isCompleted = stepNumber < currentStep
                    const isLast = index === steps.length - 1

                    return (
                        <div
                            key={index}
                            className="flex items-center flex-1 last:flex-none"
                        >
                            {/* Step column (circle + label) */}
                            <div className="flex flex-col items-center">
                                {/* Circle */}
                                <div
                                    className={cn(
                                        'w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium transition-all cursor-pointer shrink-0',
                                        isCompleted &&
                                            'bg-green-500 text-white',
                                        isActive &&
                                            'bg-blue-600 text-white ring-2 ring-blue-100',
                                        !isActive &&
                                            !isCompleted &&
                                            'bg-gray-200 text-gray-500',
                                        onChange && 'hover:opacity-80'
                                    )}
                                    onClick={() => onChange?.(stepNumber)}
                                >
                                    {isCompleted ? (
                                        <CheckIcon className="w-3.5 h-3.5" />
                                    ) : step.icon ? (
                                        <span className="[&>svg]:w-3.5 [&>svg]:h-3.5">
                                            {step.icon}
                                        </span>
                                    ) : (
                                        stepNumber
                                    )}
                                </div>
                                {/* Label */}
                                <span
                                    className={cn(
                                        'text-[11px] font-medium text-center mt-1.5 max-w-[120px]',
                                        isActive
                                            ? 'text-blue-600'
                                            : 'text-gray-500'
                                    )}
                                >
                                    {step.label}
                                </span>
                            </div>

                            {/* Connector line */}
                            {!isLast && (
                                <div
                                    className={cn(
                                        'flex-1 h-1 mx-2 rounded-full transition-all self-start mt-[14px]',
                                        stepNumber < currentStep
                                            ? 'bg-green-500'
                                            : 'bg-gray-200'
                                    )}
                                />
                            )}
                        </div>
                    )
                })}
            </div>
        </div>
    )
}

export default StepIndicator
